import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { selectedDepartmentTypes } from '../../types/shop';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { H3, Button, Currency, Icon, P, ProductGenderDivision, Spinner } from '../../components';
import ImageSlider from '~COMPONENTS/imageSlider/ImageSlider';
import { ProductImage } from '../../components/aurora';
import ImageLabelOverlay from '../../components/ImageLabelOverlay';
import { productTypes } from '../../types/products';
import { getFundamentals } from '../../fundamentals/constants';
import { ORDER_TYPE_FUTURES } from '../constants';
import { getSelectedDepartment, selectIncludeUnavailableProduct } from '../selectors';
import { createProductName } from '../../util/createProductName';
import { mapAvailabilityToTranslationString } from '../util/helpers';
import { FUNDAMENTAL_DATA_TYPE } from '../../fundamentals/types';
import { addProducts, EVENT_GROUP_ADD_TO_CART } from '../../carts/actions';
import { getAllotmentsForSelectedDepartment, getIsGreaterChinaSalesOrg } from '../../gamedayAccount/selectors';
import { cartIsLoading, filterUnitAllotmentsForProduct } from '../../carts/constants';
import Truncator from '../../components/Truncator';
import { sendEventToGA, GA_EVENT_GRIDWALL_ADD_TO_CART_CLICK } from '../../analytics/constants';

export const buildButtonTranslationId = (isCustomizable, inCart, productAvailability) => {
  if (productAvailability === 'Available Soon' || productAvailability === 'Out of Stock') {
    return mapAvailabilityToTranslationString(productAvailability);
  }
  if (isCustomizable) {
    return 'shop.cart.customize';
  }
  if (inCart) {
    return 'shop.cart.in';
  }

  return 'shop.cart.add';
};

export class ProductCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedProduct: props.products[0],
      isImageBroken: false,
      multiColor: !!props.products.length,
      previewProductCode: null,
      inCart: false,
      isLoading: false
    };
  }

  setImageFallback = () => this.setState({ isImageBroken: true });

  setSelectedProduct = selectedProduct => this.setState({ selectedProduct });

  handleMouseLeave = () => {
    this.setState(state => {
      const {
        selectedProduct: { productCode }
      } = state;

      return { previewProductCode: productCode };
    });
  };

  previewProduct = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      this.setSelectedProduct(product);
    }
    const previewProductCode = product ? product.productCode : null;
    this.setState({
      isImageBroken: false,
      previewProductCode
    });
  };

  handleAddToCart = async e => {
    e.stopPropagation();
    const { addProducts } = this.props;
    const { selectedProduct } = this.state;

    this.setState({ isLoading: true }, async () => {
      await addProducts([selectedProduct]);
      this.setState({ isLoading: false });
    });
  };

  handleButtonClick = e => {
    const { handleProductClick } = this.props;
    const { selectedProduct } = this.state;

    if (this.hasFundamentalData()) {
      handleProductClick(selectedProduct.productCode);
    } else {
      this.handleAddToCart(e);
    }

    sendEventToGA(GA_EVENT_GRIDWALL_ADD_TO_CART_CLICK);
  };

  /**
   * Returns true if the product code is found within the account's
   * fundamental data AND the selected department type is futures
   * @returns {boolean}
   */
  hasFundamentalData() {
    const { fundamentalData, selectedDepartment } = this.props;
    const { selectedProduct } = this.state;

    return (
      selectedDepartment.type === ORDER_TYPE_FUTURES &&
      (selectedProduct.isFundamental ||
        (fundamentalData.styleColorCodes &&
          Object.keys(fundamentalData.styleColorCodes).includes(selectedProduct.productCode)))
    );
  }

  unitAllotmentsForProduct() {
    const { selectedProduct } = this.state;
    const { unitAllotments } = this.props;

    return filterUnitAllotmentsForProduct(selectedProduct, unitAllotments);
  }

  render() {
    const {
      products,
      handleProductClick,
      isGreaterChinaSalesOrg,
      selectedDepartment,
      styleNumber,
      isPending,
      includeUnavailableProduct
    } = this.props;
    const { selectedProduct, isImageBroken, multiColor, previewProductCode, inCart, isLoading } = this.state;
    const currPrice = selectedProduct.prices.filter(price => price.currency === selectedDepartment.code)[0];
    const productName = (selectedProduct.copy && selectedProduct.copy.name) || 'Product';
    const rawName = (selectedProduct && selectedProduct.rawName) || '';
    const productDimensions =
      selectedProduct.dimensions || (selectedProduct.metaData && selectedProduct.metaData.dimensions);
    const productDivision = selectedProduct.division || (selectedProduct.metaData && selectedProduct.metaData.division);
    const displayImageProductCode = previewProductCode || selectedProduct.productCode;
    const productAvailability = products.find(p => p.productCode === displayImageProductCode)?.availabilityStatus;
    const isUnavailable = productAvailability === 'Available Soon' || productAvailability === 'Out of Stock';
    const isCustomizable = this.hasFundamentalData();
    const unitAllotments = this.unitAllotmentsForProduct();
    const derivedProductName = createProductName({
      name: productName,
      dimensions: productDimensions,
      division: productDivision
    });
    const inclUnavailParam = includeUnavailableProduct ? '?inclUnavail=1' : '';

    return (
      <li className='gridwall__card mb1-sm mb4-lg' data-desc='gridwall__productCard'>
        <span hidden data-desc='gridwall__productCard_styleNumber'>
          {styleNumber}
        </span>
        <span hidden data-desc='gridwall__productCard_mainImage_isImageBroken'>
          {isImageBroken.toString()}
        </span>
        <span hidden data-desc='gridwall__productCard_colorCount'>
          {products.length}
        </span>
        <div className={classnames('gridwall__card_inner bg-white', { hasColorSelection: multiColor })}>
          <a
            className={classnames('right__clickable__product')}
            href={`${selectedDepartment.type}/${selectedProduct.productCode}${inclUnavailParam}`}
            onClick={e => {
              handleProductClick(selectedProduct.productCode);
              e?.preventDefault();
              return false;
            }}
          >
            <div
              className={classnames('gridwall__card_image', 'flex-container center prl6-sm pt4-sm pb4-sm', {
                isUnavailable
              })}
            >
              <span hidden data-desc='gridwall__productCard_mainImage_colorCode'>
                {selectedProduct.color}
              </span>
              <span hidden data-desc='gridwall__productCard_mainImage_colorName'>
                {selectedProduct.colorName}
              </span>
              {isImageBroken ? (
                <span
                  className='u-bold text-color-secondary ta-sm-c'
                  data-desc='gridwall__productCard_mainImage-broken'
                >
                  <TranslatedCopy id='global.image' />
                  <br />
                  <TranslatedCopy id='global.unavailable' />
                </span>
              ) : (
                <ProductImage alt={displayImageProductCode} productCode={displayImageProductCode} />
              )}
            </div>
            {isUnavailable && (
              <ImageLabelOverlay
                text={new TranslatedCopy({ id: mapAvailabilityToTranslationString(productAvailability) }).toString()}
              />
            )}
          </a>
          <div
            className={classnames(
              'gridwall__card_color-selection',
              'flex-container row space-between pr3-sm pl3-sm pt2-sm text-color-secondary border-top'
            )}
            data-desc='gridwall__productCard_manyColors'
          >
            <span className='body-3' data-desc='gridwall__productCard_oneColor'>
              {multiColor && <TranslatedCopy id='gridwall.card.colors' values={{ amount: products.length }} />}
            </span>
            <div>
              {unitAllotments.length > 0 && (
                <div className='gridwall__card_icon'>
                  <Icon name='box' alt={new TranslatedCopy({ id: 'global.unitAllotments' }).toString()} />
                </div>
              )}
              {selectedProduct.hasPurchaseOptions && (
                <div className='gridwall__card_icon'>
                  <Icon name='filled-dollar' alt={new TranslatedCopy({ id: 'global.purchaseOptions' }).toString()} />
                </div>
              )}
              {isCustomizable && (
                <div className='gridwall__card_icon'>
                  <Icon name='customizable' alt={new TranslatedCopy({ id: 'global.product.fundamental' }).toString()} />
                </div>
              )}
              {!selectedProduct.returnable && (
                <div className='gridwall__card_icon'>
                  <Icon name='non-returnable' alt={new TranslatedCopy({ id: 'global.nonReturnable' }).toString()} />
                </div>
              )}
            </div>
          </div>

          <div className='d-sm-h d-md-b'>
            {products && products.length > 1 && (
              <ImageSlider
                products={products}
                selectedProduct={selectedProduct}
                previewProduct={this.previewProduct}
                handleProductClick={handleProductClick}
                handleMouseLeave={this.handleMouseLeave}
              />
            )}
          </div>
          <div className='mr3-sm ml3-sm pt2-sm'>
            <H3 data-desc='gridwall__productCard_name'>
              <Truncator value={`${derivedProductName}`} />
            </H3>
            <p className='text-color-grey body-4'>
              <Truncator value={rawName} />
            </p>
            <p
              className='gridwall__card_genderSilhouette mt1-sm body-4 u-capitalize'
              data-desc='gridwall__productCard_style'
            >
              <ProductGenderDivision
                gender={selectedProduct.styleGenderAge}
                division={selectedProduct.division}
                sport={selectedProduct.sport}
              />
            </p>
            {selectedProduct.prices && currPrice && (
              <div className='mt1-sm flex-container row'>
                <P className='lh10-sm u-bold' data-desc='gridwall__productCard_price-wholesale'>
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  <TranslatedCopy id='global.product.wholesale.card' />{' '}
                  {currPrice.wholesale && <Currency value={currPrice.wholesale} type={currPrice.currency} />}
                </P>
                {!isGreaterChinaSalesOrg && (
                  <P
                    className='lh10-sm text-color-secondary ml1-sm u-bold'
                    data-desc='gridwall__productCard_price-retail'
                  >
                    {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                    {'/ '}
                    <TranslatedCopy id='global.product.retail.card' />{' '}
                    {currPrice.retail && <Currency value={currPrice.retail} type={currPrice.currency} />}
                  </P>
                )}
              </div>
            )}
            {(previewProductCode || selectedProduct.productCode) && (
              <div className='mt1-sm'>
                <P className='lh10-sm u-bold' data-desc='gridwall__productCard_code-product'>
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  <TranslatedCopy id='global.product.style' /> {previewProductCode || selectedProduct.productCode}
                </P>
              </div>
            )}
            <Button
              extraClassName='ncss-btn-primary-dark u-full-width mt4-sm gridwall__card_addButton d-sm-h d-md-b'
              data-desc='gridwall__productCard_addToCart'
              onClick={this.handleButtonClick}
              disabled={isPending || isUnavailable}
            >
              {isLoading && (
                <div className='flex-container center'>
                  <Spinner />
                </div>
              )}
              <TranslatedCopy id={buildButtonTranslationId(isCustomizable, inCart, productAvailability)} />
            </Button>
          </div>
        </div>
      </li>
    );
  }
}

ProductCard.defaultProps = {
  selectedDepartment: null
};

ProductCard.propTypes = {
  addProducts: PropTypes.func.isRequired,
  handleProductClick: PropTypes.func.isRequired,
  fundamentalData: FUNDAMENTAL_DATA_TYPE.isRequired,
  includeUnavailableProduct: PropTypes.bool.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(productTypes).isRequired,
  selectedDepartment: selectedDepartmentTypes,
  styleNumber: PropTypes.string.isRequired,
  unitAllotments: PropTypes.array.isRequired
};

export const mapStateToProps = state => ({
  fundamentalData: getFundamentals(state),
  includeUnavailableProduct: selectIncludeUnavailableProduct(state),
  isGreaterChinaSalesOrg: getIsGreaterChinaSalesOrg(state),
  isPending: cartIsLoading(state, EVENT_GROUP_ADD_TO_CART),
  unitAllotments: getAllotmentsForSelectedDepartment(state).unitAllotments,
  selectedDepartment: getSelectedDepartment(state)
});

const connector = connect(mapStateToProps, { addProducts });

export default connector(ProductCard);
